import { HOTSPOTS } from "../../config/hotspots";

/**
 * HotspotHelper
 *
 * Provides geographic boundary-check utilities for determining whether
 * a user's location falls within any configured hotspot area.
 *
 * Uses the Haversine formula for accurate great-circle distance
 * calculations between two geographic coordinates.
 */
class HotspotHelper {
    /**
     * Calculates the great-circle distance between two geographic coordinates
     * using the Haversine formula.
     *
     * @param lat1 Latitude of the first point (degrees)
     * @param lon1 Longitude of the first point (degrees)
     * @param lat2 Latitude of the second point (degrees)
     * @param lon2 Longitude of the second point (degrees)
     * @returns Distance between the two points in kilometers
     */
    public static getDistanceFromLatLonInKm(
        lat1: number,
        lon1: number,
        lat2: number,
        lon2: number
    ): number {
        const R = 6371; // Earth's mean radius in km
        const dLat = this.deg2rad(lat2 - lat1);
        const dLon = this.deg2rad(lon2 - lon1);
        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(this.deg2rad(lat1)) *
            Math.cos(this.deg2rad(lat2)) *
            Math.sin(dLon / 2) *
            Math.sin(dLon / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    /**
     * Converts degrees to radians.
     */
    private static deg2rad(deg: number): number {
        return deg * (Math.PI / 180);
    }

    /**
     * Checks whether the given coordinates fall inside ANY of the configured
     * hotspot areas. Returns true as soon as a match is found (short-circuit).
     *
     * @param lat User's latitude
     * @param lng User's longitude
     * @returns true if the coordinates are inside at least one hotspot
     */
    public static isInsideHotspot(lat: number, lng: number): boolean {
        return HOTSPOTS.some((hotspot) => {
            const distance = this.getDistanceFromLatLonInKm(
                lat,
                lng,
                hotspot.latitude,
                hotspot.longitude
            );
            return distance <= hotspot.radiusKm;
        });
    }
}

export default HotspotHelper;
